import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Send, CheckCircle2, ArrowLeft } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { StarInput } from "@/components/Stars";
import { api, errorMessage } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const MAX = 600;

export default function ReviewPage() {
  useEffect(() => { document.title = "Leave a review — Underdog Podcasts"; }, []);
  const { homepage } = useSettings();
  const [params] = useSearchParams();
  const [shows, setShows] = useState([]);
  const [linked, setLinked] = useState(null); // the show named in ?show=, loaded directly so the page is pre-filled instantly
  const [form, setForm] = useState({ name: "", rating: 0, text: "", email: "", website: "", show_slug: params.get("show") || "" });
  useEffect(() => { api.get("/shows").then((r) => setShows([...r.data].sort((a, b) => a.name.localeCompare(b.name)))).catch(() => {}); }, []);
  useEffect(() => {
    const slug = params.get("show");
    if (!slug) return;
    api.get(`/shows/${slug}`).then((r) => setLinked({ id: r.data.id, slug: r.data.slug, name: r.data.name })).catch(() => setForm((f) => ({ ...f, show_slug: "" })));
  }, [params]);
  const options = linked && !shows.some((s) => s.slug === linked.slug) ? [linked, ...shows] : shows;
  const show = options.find((s) => s.slug === form.show_slug);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.rating) return toast.error("Tap the stars to pick a rating");
    if (!form.name.trim() || !form.text.trim()) return toast.error("Add your name and a few words");
    setBusy(true);
    try { await api.post("/testimonials", form); setDone(true); }
    catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen flex flex-col" data-testid="review-page">
      <Navbar />
      <main className="flex-1 max-w-2xl mx-auto w-full px-5 sm:px-8 py-12">
        {done ? (
          <div className="card-surface rounded-3xl p-8 text-center" data-testid="review-thanks">
            <CheckCircle2 size={44} className="mx-auto text-emerald-400" />
            <h1 className="mt-4 font-display text-3xl sm:text-4xl font-black tracking-tight">Thank you!</h1>
            <p className="mt-3 text-slate-400">Your review is in. We read every one, and it'll appear on the homepage{show ? ` and on the ${show.name} page` : ""} once it's approved.</p>
            <Link to="/" className="mt-6 inline-flex items-center gap-2 rounded-full bg-sky-400 px-5 py-2.5 text-sm font-bold text-[#070F1E] hover:bg-sky-300" data-testid="review-back-home"><ArrowLeft size={15} /> Back to home</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-7" data-testid="review-form">
            <div>
              <p className="eyebrow mb-2">{homepage.reviews_eyebrow || "Listener love"}</p>
              <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight" data-testid="review-heading">{show ? <>Review <span className="text-amber-300">{show.name}</span></> : homepage.reviews_button || "Leave a review"}</h1>
              <p className="mt-3 text-slate-400">{show ? <>You're reviewing <b className="text-slate-200" data-testid="review-show-name">{show.name}</b>. </> : "Tell other listeners what you think of the Underdog shows. "}Reviews are read by a human before they go live.</p>
            </div>
            <div>
              <Label htmlFor="rv-show">Which show? <span className="text-slate-500 font-normal">(optional)</span></Label>
              <select id="rv-show" value={form.show_slug} onChange={(e) => set("show_slug", e.target.value)} className="mt-1.5 h-10 w-full rounded-md bg-white/5 border border-white/10 px-3 text-sm text-white [color-scheme:dark]" data-testid="review-show">
                <option value="" className="bg-[#0B1A33] text-white">The Whole Network</option>
                {options.map((s) => <option key={s.id} value={s.slug} className="bg-[#0B1A33] text-white">{s.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Your rating</Label>
              <p className="text-xs text-slate-500 mb-2">Tap the left half of a star for a half star, the right half for a full one.</p>
              <StarInput value={form.rating} onChange={(v) => set("rating", v)} testId="review-stars" />
            </div>
            <div><Label htmlFor="rv-name">Your name</Label><Input id="rv-name" value={form.name} onChange={(e) => set("name", e.target.value)} maxLength={80} placeholder="e.g. John Smith" className="mt-1.5 bg-white/5 border-white/10" data-testid="review-name" /></div>
            <div>
              <div className="flex items-baseline justify-between"><Label htmlFor="rv-text">Your review</Label><span className={`text-xs ${form.text.length > MAX - 40 ? "text-amber-300" : "text-slate-500"}`} data-testid="review-count">{form.text.length}/{MAX}</span></div>
              <textarea id="rv-text" value={form.text} onChange={(e) => set("text", e.target.value.slice(0, MAX))} rows={5} placeholder="What do you love? What keeps you coming back?" className="mt-1.5 w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm leading-relaxed" data-testid="review-text" />
            </div>
            <div><Label htmlFor="rv-email">Email <span className="text-slate-500 font-normal">(optional, never shown publicly)</span></Label><Input id="rv-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" className="mt-1.5 bg-white/5 border-white/10" data-testid="review-email" /></div>
            <input type="text" name="website" value={form.website} onChange={(e) => set("website", e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 opacity-0" data-testid="review-honeypot" />
            <Button type="submit" disabled={busy} className="rounded-full bg-amber-400 text-[#1a1200] font-bold hover:bg-amber-300 px-6" data-testid="review-submit">{busy ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Send size={15} className="mr-2" />} Send review</Button>
          </form>
        )}
      </main>
      <Footer />
    </div>
  );
}
